import { Check, Copy, Loader2 } from "lucide-react";
import QRCode from "qrcode";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthApiError, useAuth } from "@/lib/auth";
import {
  acceptInvitation,
  enable2fa,
  lookupInvitation,
  setup2fa,
  type InvitationLookup,
  type TotpSetupResult,
} from "@/lib/auth/invitation-api";
import {
  clearInvitationSetup,
  readInvitationSetup,
  rememberInvitationSetup,
} from "@/lib/auth/invitation-storage";
import { cn } from "@/lib/utils";

type Step = "verify" | "credentials" | "totp";

const STEP_ORDER: Step[] = ["verify", "credentials", "totp"];
const STEP_LABELS: Record<Step, string> = {
  verify: "Verify invitation",
  credentials: "Set up account",
  totp: "Enable 2FA",
};

function lookupMessage(err: AuthApiError): string {
  switch (err.code) {
    case "not_found":
      return "This invitation link is not valid. Ask your administrator to send a new one.";
    case "expired":
    case "already_used":
      return "This invitation has expired or has already been used. Ask your administrator to send a new one.";
    default:
      return err.serverMessage ?? "Couldn't validate this invitation. Please try again.";
  }
}

function acceptMessage(err: AuthApiError): string {
  switch (err.code) {
    case "not_found":
      return "This invitation link is not valid. Ask your administrator to send a new one.";
    case "expired":
    case "already_used":
      return "This invitation has expired or has already been used. Ask your administrator to send a new one.";
    case "user_exists":
      return "An account with this email already exists. Try signing in instead.";
    case "validation_failed":
      return "Please check your name and password and try again.";
    default:
      return err.serverMessage ?? "Couldn't accept the invitation. Please try again.";
  }
}

function setupMessage(err: AuthApiError): string {
  switch (err.code) {
    case "invalid_mfa_session":
    case "unauthorized":
      return "Your setup session has expired. Please re-enter your password to continue.";
    default:
      return err.serverMessage ?? "Couldn't start 2FA setup. Please try again.";
  }
}

function enableMessage(err: AuthApiError): string {
  switch (err.code) {
    case "invalid_code":
      return "That code didn't match. Try again with a fresh code from your authenticator app.";
    case "rate_limited":
      return "Too many incorrect codes. Please wait a minute and try again.";
    case "invalid_mfa_session":
    case "unauthorized":
      return "Your setup session has expired. Please re-enter your password to continue.";
    case "setup_required":
      return "We couldn't find a pending 2FA setup. Please reload and try again.";
    default:
      return err.serverMessage ?? "Couldn't verify the code. Please try again.";
  }
}

interface PasswordChecks {
  length: boolean;
  letter: boolean;
  digit: boolean;
}

function checkPassword(value: string): PasswordChecks {
  return {
    length: value.length >= 10,
    letter: /[A-Za-z]/.test(value),
    digit: /[0-9]/.test(value),
  };
}

function isPasswordValid(checks: PasswordChecks): boolean {
  return checks.length && checks.letter && checks.digit;
}

export function InvitationAcceptPage() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { setAuthed } = useAuth();

  const [step, setStep] = useState<Step>("verify");
  const [invitation, setInvitation] = useState<InvitationLookup | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(true);

  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [credentialsError, setCredentialsError] = useState<string | null>(null);
  const [submittingCredentials, setSubmittingCredentials] = useState(false);

  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [totpSetup, setTotpSetup] = useState<TotpSetupResult | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [totpError, setTotpError] = useState<string | null>(null);
  const [setupLoading, setSetupLoading] = useState(false);
  const [code, setCode] = useState("");
  const [submittingCode, setSubmittingCode] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);

  const passwordChecks = useMemo(() => checkPassword(password), [password]);
  const passwordOk = isPasswordValid(passwordChecks);
  const confirmMismatch = confirm.length > 0 && confirm !== password;

  // Step 1 — validate the invitation token on mount. If a setup_token was
  // already stashed for this invitation (mid-flow refresh), skip straight to
  // step 3 and let the setup-fetch effect pick up.
  useEffect(() => {
    if (!token) {
      setVerifyError("This invitation link is missing a token.");
      setVerifying(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const info = await lookupInvitation(token);
        if (cancelled) return;
        setInvitation(info);
        const stashed = readInvitationSetup(token);
        if (stashed) {
          setSetupToken(stashed);
          setStep("totp");
        } else {
          setStep("credentials");
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof AuthApiError) {
          setVerifyError(lookupMessage(err));
        } else {
          setVerifyError("Couldn't reach the server. Check your connection and retry.");
        }
      } finally {
        if (!cancelled) setVerifying(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Step 3 setup — fetch the otpauth URL + secret once we have a setup token.
  useEffect(() => {
    if (step !== "totp" || !setupToken || totpSetup) return;
    let cancelled = false;
    setSetupLoading(true);
    setTotpError(null);
    (async () => {
      try {
        const result = await setup2fa({ mfaSession: setupToken });
        if (cancelled) return;
        setTotpSetup(result);
        const dataUrl = await QRCode.toDataURL(result.otpauthUrl, {
          width: 192,
          margin: 1,
          errorCorrectionLevel: "M",
        });
        if (cancelled) return;
        setQrDataUrl(dataUrl);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof AuthApiError) {
          setTotpError(setupMessage(err));
          if (err.code === "invalid_mfa_session" || err.code === "unauthorized") {
            clearInvitationSetup(token);
            setSetupToken(null);
            setStep("credentials");
          }
        } else {
          setTotpError("Couldn't reach the server. Check your connection and retry.");
        }
      } finally {
        if (!cancelled) setSetupLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, setupToken, totpSetup, token]);

  const handleCredentialsSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (submittingCredentials) return;
      const trimmedName = name.trim();
      if (!trimmedName) {
        setCredentialsError("Please enter your name.");
        return;
      }
      if (!passwordOk) {
        setCredentialsError("Please choose a password that meets all the requirements.");
        return;
      }
      if (confirm !== password) {
        setCredentialsError("Passwords don't match.");
        return;
      }
      setCredentialsError(null);
      setSubmittingCredentials(true);
      try {
        const result = await acceptInvitation({
          token,
          name: trimmedName,
          password,
        });
        rememberInvitationSetup(token, result.setupToken);
        setSetupToken(result.setupToken);
        setStep("totp");
      } catch (err) {
        if (err instanceof AuthApiError) {
          setCredentialsError(acceptMessage(err));
          if (err.code === "expired" || err.code === "already_used" || err.code === "not_found") {
            setStep("verify");
            setVerifyError(acceptMessage(err));
          }
        } else {
          setCredentialsError("Couldn't reach the server. Check your connection and retry.");
        }
      } finally {
        setSubmittingCredentials(false);
      }
    },
    [confirm, name, password, passwordOk, submittingCredentials, token],
  );

  const handleTotpSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (submittingCode || !setupToken) return;
      const normalized = code.replace(/\D/g, "");
      if (normalized.length !== 6) {
        setTotpError("Enter the 6-digit code from your authenticator app.");
        return;
      }
      setTotpError(null);
      setSubmittingCode(true);
      try {
        const result = await enable2fa({
          mfaSession: setupToken,
          code: normalized,
        });
        clearInvitationSetup(token);
        setAuthed({ accessToken: result.accessToken, user: result.user });
        navigate("/", { replace: true });
      } catch (err) {
        if (err instanceof AuthApiError) {
          setTotpError(enableMessage(err));
          if (err.code === "invalid_mfa_session" || err.code === "unauthorized") {
            clearInvitationSetup(token);
            setSetupToken(null);
            setStep("credentials");
          }
        } else {
          setTotpError("Couldn't reach the server. Check your connection and retry.");
        }
      } finally {
        setSubmittingCode(false);
      }
    },
    [code, navigate, setAuthed, setupToken, submittingCode, token],
  );

  const copySecret = useCallback(async () => {
    if (!totpSetup) return;
    try {
      await navigator.clipboard.writeText(totpSetup.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 1500);
    } catch {
      // Clipboard can be blocked (insecure context, permissions). The secret
      // is shown in plain text already, so silently fail.
    }
  }, [totpSetup]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-1 text-center">
          <p className="font-mono text-xs uppercase tracking-wide text-muted-foreground">
            Finance
          </p>
          <h1 className="text-xl font-semibold tracking-tight">
            Accept your invitation
          </h1>
        </div>

        <StepIndicator current={step} />

        {step === "verify" ? (
          <VerifyCard
            verifying={verifying}
            error={verifyError}
            invitation={invitation}
          />
        ) : null}

        {step === "credentials" && invitation ? (
          <CredentialsCard
            invitation={invitation}
            name={name}
            password={password}
            confirm={confirm}
            checks={passwordChecks}
            passwordOk={passwordOk}
            confirmMismatch={confirmMismatch}
            error={credentialsError}
            submitting={submittingCredentials}
            onNameChange={setName}
            onPasswordChange={setPassword}
            onConfirmChange={setConfirm}
            onSubmit={handleCredentialsSubmit}
          />
        ) : null}

        {step === "totp" ? (
          <TotpCard
            invitation={invitation}
            setupLoading={setupLoading}
            setup={totpSetup}
            qrDataUrl={qrDataUrl}
            code={code}
            error={totpError}
            submitting={submittingCode}
            secretCopied={secretCopied}
            onCodeChange={setCode}
            onCopySecret={copySecret}
            onSubmit={handleTotpSubmit}
          />
        ) : null}
      </div>
    </div>
  );
}

function StepIndicator({ current }: { current: Step }) {
  const currentIndex = STEP_ORDER.indexOf(current);
  return (
    <ol className="flex items-center gap-2 text-xs" aria-label="Progress">
      {STEP_ORDER.map((s, i) => {
        const done = i < currentIndex;
        const active = i === currentIndex;
        return (
          <li key={s} className="flex flex-1 items-center gap-2">
            <span
              className={cn(
                "flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-medium",
                done && "border-primary bg-primary text-primary-foreground",
                active && "border-primary text-primary",
                !done && !active && "border-border text-muted-foreground",
              )}
              aria-current={active ? "step" : undefined}
            >
              {done ? <Check className="h-3 w-3" /> : i + 1}
            </span>
            <span
              className={cn(
                "truncate",
                active ? "text-foreground" : "text-muted-foreground",
              )}
            >
              {STEP_LABELS[s]}
            </span>
            {i < STEP_ORDER.length - 1 ? (
              <span
                className={cn(
                  "ml-1 h-px flex-1",
                  done ? "bg-primary" : "bg-border",
                )}
                aria-hidden
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function VerifyCard({
  verifying,
  error,
  invitation,
}: {
  verifying: boolean;
  error: string | null;
  invitation: InvitationLookup | null;
}) {
  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-sm normal-case tracking-normal text-foreground">
          Invitation
        </CardTitle>
        <CardDescription>
          We're checking your invitation link.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {verifying ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Validating invitation…
          </div>
        ) : error ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </p>
        ) : invitation ? (
          <InvitationSummary invitation={invitation} />
        ) : null}
      </CardContent>
    </Card>
  );
}

function InvitationSummary({ invitation }: { invitation: InvitationLookup }) {
  return (
    <dl className="grid grid-cols-[5rem_1fr] gap-y-2 text-sm">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        Workspace
      </dt>
      <dd className="text-foreground">Finance</dd>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        Email
      </dt>
      <dd className="font-mono text-foreground">{invitation.email}</dd>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        Role
      </dt>
      <dd className="capitalize text-foreground">{invitation.role}</dd>
    </dl>
  );
}

function CredentialsCard(props: {
  invitation: InvitationLookup;
  name: string;
  password: string;
  confirm: string;
  checks: PasswordChecks;
  passwordOk: boolean;
  confirmMismatch: boolean;
  error: string | null;
  submitting: boolean;
  onNameChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onConfirmChange: (v: string) => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}) {
  const {
    invitation,
    name,
    password,
    confirm,
    checks,
    passwordOk,
    confirmMismatch,
    error,
    submitting,
    onNameChange,
    onPasswordChange,
    onConfirmChange,
    onSubmit,
  } = props;
  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-sm normal-case tracking-normal text-foreground">
          Set up your account
        </CardTitle>
        <CardDescription>
          You're joining Finance as{" "}
          <span className="font-mono text-foreground">{invitation.email}</span>{" "}
          ({invitation.role}). Choose a password to continue.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div className="space-y-1.5">
            <Label htmlFor="name">Full name</Label>
            <Input
              id="name"
              type="text"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => onPasswordChange(e.target.value)}
              disabled={submitting}
              aria-describedby="password-rules"
            />
            <ul
              id="password-rules"
              className="space-y-1 text-xs text-muted-foreground"
            >
              <PasswordRule met={checks.length} label="At least 10 characters" />
              <PasswordRule met={checks.letter} label="Contains a letter" />
              <PasswordRule met={checks.digit} label="Contains a number" />
            </ul>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm">Confirm password</Label>
            <Input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => onConfirmChange(e.target.value)}
              disabled={submitting}
              aria-invalid={confirmMismatch || undefined}
            />
            {confirmMismatch ? (
              <p className="text-xs text-destructive">Passwords don't match.</p>
            ) : null}
          </div>

          {error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
            >
              {error}
            </p>
          ) : null}

          <Button
            type="submit"
            className="w-full"
            disabled={
              submitting || !passwordOk || confirm !== password || !name.trim()
            }
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating account…
              </>
            ) : (
              "Continue to 2FA setup"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function PasswordRule({ met, label }: { met: boolean; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <span
        className={cn(
          "flex h-3.5 w-3.5 items-center justify-center rounded-full border",
          met
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border bg-background",
        )}
        aria-hidden
      >
        {met ? <Check className="h-2.5 w-2.5" /> : null}
      </span>
      <span className={cn(met ? "text-foreground" : "text-muted-foreground")}>
        {label}
      </span>
    </li>
  );
}

function TotpCard(props: {
  invitation: InvitationLookup | null;
  setupLoading: boolean;
  setup: TotpSetupResult | null;
  qrDataUrl: string | null;
  code: string;
  error: string | null;
  submitting: boolean;
  secretCopied: boolean;
  onCodeChange: (v: string) => void;
  onCopySecret: () => void;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}) {
  const {
    setupLoading,
    setup,
    qrDataUrl,
    code,
    error,
    submitting,
    secretCopied,
    onCodeChange,
    onCopySecret,
    onSubmit,
  } = props;

  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="text-sm normal-case tracking-normal text-foreground">
          Enable two-factor authentication
        </CardTitle>
        <CardDescription>
          Scan the QR code with Google Authenticator, 1Password, Authy, or any
          other TOTP app. Then enter the 6-digit code it shows.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {setupLoading || !setup ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Preparing your authenticator setup…
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-col items-center gap-3 rounded-md border border-border bg-muted/30 p-4">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  width={192}
                  height={192}
                  alt="Scan with your authenticator app"
                  className="rounded-sm bg-white p-2"
                />
              ) : (
                <div className="flex h-48 w-48 items-center justify-center text-xs text-muted-foreground">
                  Generating QR…
                </div>
              )}
              <div className="w-full space-y-1.5">
                <Label
                  htmlFor="totp-secret"
                  className="text-[10px]"
                >
                  Or enter this key manually
                </Label>
                <div className="flex items-stretch gap-1.5">
                  <Input
                    id="totp-secret"
                    readOnly
                    value={setup.secret}
                    className="font-mono text-xs tracking-wider"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={onCopySecret}
                    aria-label="Copy secret to clipboard"
                  >
                    {secretCopied ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="code">6-digit code</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(e) =>
                    onCodeChange(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  disabled={submitting}
                  className="font-mono text-base tracking-widest"
                  aria-describedby="code-help"
                />
                <p
                  id="code-help"
                  className="text-xs text-muted-foreground"
                >
                  The code rotates every 30 seconds.
                </p>
              </div>

              {error ? (
                <p
                  role="alert"
                  className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
                >
                  {error}
                </p>
              ) : null}

              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  "Enable 2FA and sign in"
                )}
              </Button>
            </form>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
